import React from 'react';
import { FieldSchema, getByKey, setByKey } from './sectionSchema';
import TextField   from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import TextArea    from '../../../../../../component-controller-mod/component-serv/input-fs-cp/textarea-field-op/ts/textArea';
import SelectField from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField';
import { Toggle }  from '@core/uilibrary';
import LabelRow    from './labelRow';
import SplitRow    from './splitRow';
import ImageUpload from './imageUpload';
import { ListItem, IconItem } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface RendererProps {
  field:              FieldSchema;
  sectionData:        Record<string, unknown>;
  editValues:         Record<string, unknown>;
  onChange:           (key: string, v: unknown) => void;
  toggles:            Record<string, boolean>;
  onToggle:           (key: string, v: boolean) => void;
  lists:              ListItem[];
  selectedIdx:        number;
  cardVisible:        boolean;
  onCardVisible:      (v: boolean) => void;
  onCardSelect:       (i: number) => void;
  onListItemChange:   (key: string, v: unknown) => void;
  listItemEditValues: Record<string, unknown>;
  pageLabel:          string;
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldRenderer
// ─────────────────────────────────────────────────────────────────────────────

const FieldRenderer: React.FC<RendererProps> = (p) => {
  const { field, sectionData, editValues } = p;

  const savedRaw  = getByKey(sectionData, field.key);
  const editedRaw = getByKey(editValues,  field.key);
  const savedStr  = savedRaw  != null ? String(savedRaw)  : '';
  const editedStr = editedRaw != null ? String(editedRaw) : savedStr;

  // ── image ──────────────────────────────────────────────────────────────────
  if (field.type === 'image') {
    return (
      <ImageUpload
        label={field.label}
        currentSrc={savedStr || undefined}
        currentName={savedStr ? savedStr.split('/').pop() : undefined}
        onFile={f => console.log()}
      />
    );
  }

  // ── list ───────────────────────────────────────────────────────────────────
  if (field.type === 'list') {
    const item = p.lists[p.selectedIdx];
    return (
      <div className="cse-list-block">

        {/* Card selector row */}
        <div className="cse-card-selector-row">
          <span className="cse-field-label">{field.label}</span>
          <div className="cse-card-selector-right">
            <SelectField
              className="cse-card-select"
              value={String(p.selectedIdx)}
              options={p.lists.map((l, i) => ({
                value: String(i),
                label: `${p.pageLabel} / ${l.title}`,
              }))}
              onChange={v => p.onCardSelect(Number(v))}
            />
            <Toggle checked={p.cardVisible} onChange={p.onCardVisible} />
          </div>
        </div>
        <div className="cse-divider" />

        {/* Sub-fields for the selected card */}
        {item && field.listFields.map(sub => {
          const subSaved  = getByKey(item as unknown as Record<string, unknown>, sub.key);
          const subEdited = getByKey(p.listItemEditValues, sub.key);
          const subSavedStr  = subSaved  != null ? String(subSaved)  : '';
          const subEditedStr = subEdited != null ? String(subEdited) : subSavedStr;
          const isMultiline  = sub.type === 'textarea';

          const currentDisp = sub.key === 'title'
            ? `${p.pageLabel.charAt(0)}/ ${item.title}`
            : subSavedStr;

          if (sub.type === 'image') {
            return (
              <ImageUpload
                key={sub.key}
                label={sub.label}
                currentSrc={subSavedStr || undefined}
                currentName={subSavedStr ? `${item.title}.jpg` : undefined}
                onFile={f => console.log()}
              />
            );
          }

          return (
            <div key={sub.key}>
              <LabelRow label={sub.label} required={sub.required} />
              <SplitRow currentText={currentDisp} multiline={isMultiline}>
                {isMultiline ? (
                  <TextArea
                    value={subEditedStr}
                    placeholder={sub.placeholder ?? 'Enter description'}
                    onChange={v => p.onListItemChange(sub.key, v)}
                    rows={3}
                  />
                ) : (
                  <TextField
                    value={subEditedStr}
                    placeholder={sub.placeholder ?? 'Navigation name'}
                    onChange={v => p.onListItemChange(sub.key, v)}
                  />
                )}
              </SplitRow>
            </div>
          );
        })}

        {/* Service / icon items */}
        {item?.items && item.items.length > 0 && (
          <div className="cse-service-items">
            {item.items.map((ic: IconItem, j: number) => {
              const labelKey = `items.${j}.label`;
              const labelRaw = getByKey(p.listItemEditValues, labelKey);
              const svcLabel = labelRaw != null ? String(labelRaw) : ic.label;

              return (
                <div key={ic.id} className="cse-service-block">
                  <LabelRow
                    label={`Service${j + 1}`}
                    toggle={{
                      checked:  p.toggles[`svc_${p.selectedIdx}_${j}`] ?? true,
                      onChange: v => p.onToggle(`svc_${p.selectedIdx}_${j}`, v),
                    }}
                  />
                  <SplitRow currentText="">
                    <div className="cse-service-cell">
                      <div className="cse-service-left">
                        {ic.Icon
                          ? <img src={ic.Icon} alt={ic.iconAlt} className="cse-service-icon"
                              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          : <div className="cse-service-icon-placeholder" />
                        }
                        <div>
                          <div className="cse-service-label">{ic.label || `Service ${j + 1}`}</div>
                          <div className="cse-service-size">5 MB of 12MB</div>
                        </div>
                      </div>
                      <TextField
                        value={svcLabel}
                        placeholder="Navigation name"
                        onChange={v => p.onListItemChange(labelKey, v)}
                      />
                    </div>
                  </SplitRow>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── select ─────────────────────────────────────────────────────────────────
  if (field.type === 'select') {
    return (
      <div>
        <LabelRow
          label={field.label}
          required={field.required}
          toggle={field.withToggle
            ? { checked: p.toggles[field.key] ?? true, onChange: v => p.onToggle(field.key, v) }
            : undefined}
        />
        <SelectField
          value={editedStr}
          options={field.options}
          onChange={v => p.onChange(field.key, v)}
        />
      </div>
    );
  }

  // ── text / textarea ────────────────────────────────────────────────────────
  const isTextarea    = field.type === 'textarea';
  const isMainContent = field.asMainContentTitle === true;
  const displayText   = isMainContent ? `[ ${savedStr} ]` : savedStr;

  return (
    <div>
      <LabelRow
        label={field.label}
        required={field.required}
        toggle={field.withToggle
          ? { checked: p.toggles[field.key] ?? true, onChange: v => p.onToggle(field.key, v) }
          : undefined}
      />
      <SplitRow currentText={displayText} multiline={isTextarea}>
        {isTextarea ? (
          <TextArea
            value={editedStr}
            placeholder={field.placeholder}
            onChange={v => p.onChange(field.key, v)}
            rows={3}
          />
        ) : (
          <TextField
            value={editedStr}
            placeholder={field.placeholder}
            onChange={v => p.onChange(field.key, v)}
          />
        )}
      </SplitRow>
    </div>
  );
};

export default FieldRenderer;